// Custom Tour Guide component using Joy UI - React 19 compatible
// Modern design with elegant callouts and smart positioning
import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Card, Typography, IconButton, Modal, ModalDialog } from '@mui/joy';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export interface TourStep {
  target: string; // CSS selector or "body" for center
  content: string;
  title?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

interface TourGuideProps {
  steps: TourStep[];
  run: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

type ComputedPlacement = 'top' | 'bottom' | 'left' | 'right' | 'center';

export default function TourGuide({ steps, run, onComplete, onSkip }: TourGuideProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [computedPlacement, setComputedPlacement] = useState<ComputedPlacement>('bottom');
  const overlayRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  // Smart placement calculation based on viewport position
  const calculateBestPlacement = (rect: DOMRect, preferred?: string): ComputedPlacement => {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const tooltipHeight = 250; // Estimated tooltip height
    const tooltipWidth = 420; // Estimated tooltip width
    const padding = 20;

    // Check available space in each direction
    const spaceTop = rect.top;
    const spaceBottom = viewportHeight - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - rect.right;

    // If preferred placement has enough space, use it
    if (preferred === 'top' && spaceTop >= tooltipHeight + padding) return 'top';
    if (preferred === 'bottom' && spaceBottom >= tooltipHeight + padding) return 'bottom';
    if (preferred === 'left' && spaceLeft >= tooltipWidth + padding) return 'left';
    if (preferred === 'right' && spaceRight >= tooltipWidth + padding) return 'right';

    // Auto-select best placement based on available space
    const spaces = {
      top: spaceTop,
      bottom: spaceBottom,
      left: spaceLeft,
      right: spaceRight
    };

    // Prioritize vertical placements for better readability
    if (spaceBottom >= tooltipHeight + padding) return 'bottom';
    if (spaceTop >= tooltipHeight + padding) return 'top';
    if (spaceRight >= tooltipWidth + padding) return 'right';
    if (spaceLeft >= tooltipWidth + padding) return 'left';

    // If no ideal space, choose the largest available
    const maxSpace = Math.max(...Object.values(spaces));
    return Object.entries(spaces).find(([_, space]) => space === maxSpace)?.[0] as ComputedPlacement || 'bottom';
  };

  // Find and scroll to target element
  useEffect(() => {
    if (!run || !step) return;

    const updateTargetPosition = () => {
      if (step.target === 'body' || step.placement === 'center') {
        setTargetRect(null); // Center modal
        setComputedPlacement('center');
        return;
      }

      const element = document.querySelector(step.target);
      if (element) {
        // Scroll element into view with padding
        element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        
        // Wait for scroll to finish before getting rect
        setTimeout(() => {
          const rect = element.getBoundingClientRect();
          setTargetRect(rect);
          
          // Calculate best placement
          const placement = calculateBestPlacement(rect, step.placement);
          setComputedPlacement(placement);
        }, 300);
      } else {
        console.warn(`Tour target not found: ${step.target}`);
        setTargetRect(null);
        setComputedPlacement('center');
      }
    };

    // Initial position
    updateTargetPosition();

    // Update on scroll/resize
    const handleUpdate = () => {
      if (step.target !== 'body' && step.placement !== 'center') {
        const element = document.querySelector(step.target);
        if (element) {
          const rect = element.getBoundingClientRect();
          setTargetRect(rect);
          const placement = calculateBestPlacement(rect, step.placement);
          setComputedPlacement(placement);
        }
      }
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [run, step]);

  const handleNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirst) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    setCurrentStep(0);
    onSkip();
  };

  if (!run || !step) return null;

  // Calculate tooltip position with smart placement
  const getTooltipStyle = (): React.CSSProperties => {
    if (!targetRect || computedPlacement === 'center') {
      return {}; // Use Modal center positioning
    }

    const offset = 20;
    let top = 0;
    let left = 0;
    let transform = '';

    switch (computedPlacement) {
      case 'top':
        top = targetRect.top - offset;
        left = targetRect.left + targetRect.width / 2;
        transform = 'translate(-50%, -100%)';
        break;
      case 'bottom':
        top = targetRect.bottom + offset;
        left = targetRect.left + targetRect.width / 2;
        transform = 'translate(-50%, 0)';
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2;
        left = targetRect.left - offset;
        transform = 'translate(-100%, -50%)';
        break;
      case 'right':
        top = targetRect.top + targetRect.height / 2;
        left = targetRect.right + offset;
        transform = 'translate(0, -50%)';
        break;
    }

    return {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      transform,
      zIndex: 10001,
      maxWidth: '420px',
      width: 'calc(100vw - 32px)', // Responsive on mobile
    };
  };

  // Render arrow - disabled, using highlight only
  const renderArrow = () => {
    return null;
  };

  const tooltipContent = (
    <Card
      ref={tooltipRef}
      variant="outlined"
      color="primary"
      sx={{
        p: 1.75,
        minWidth: 280,
        maxWidth: 380,
        borderRadius: '10px',
        borderWidth: 2,
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.18)',
        background: 'rgba(255, 255, 255, 0.98)',
        position: 'relative',
        animation: 'tourSlideIn 0.3s ease-out',
        '@keyframes tourSlideIn': {
          from: { opacity: 0, transform: 'translateY(-8px)' },
          to: { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      {/* Two-layer arrow (border + fill) */}
      {renderArrow()}

      {/* Compact header with inline counter */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.25 }}>
        <Box sx={{ flex: 1, pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {step.title && (
              <Typography 
                level="title-sm" 
                sx={{ 
                  color: 'primary.700',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  letterSpacing: '-0.01em',
                }}
              >
                {step.title}
              </Typography>
            )}
            <Typography 
              level="body-xs" 
              sx={{ 
                color: 'neutral.500',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            >
              {currentStep + 1}/{steps.length}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          <IconButton
            size="sm"
            variant="plain"
            onClick={handleSkip}
            title="Skip tour"
            sx={{ 
              minWidth: 24,
              minHeight: 24,
              color: 'neutral.500',
              '&:hover': {
                bgcolor: 'neutral.softHoverBg',
                color: 'neutral.plainColor',
              }
            }}
          >
            <CloseIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Box>
      </Box>

      {/* Compact content with proper text wrapping */}
      <Typography 
        level="body-sm" 
        sx={{ 
          mb: 1.5,
          lineHeight: 1.6,
          color: 'text.primary',
          fontSize: '0.85rem',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'normal',
        }}
      >
        {step.content}
      </Typography>

      {/* Compact footer */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        {/* Progress dots */}
        <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'center' }}>
          {steps.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: index === currentStep ? 18 : 5,
                height: 5,
                borderRadius: 2.5,
                bgcolor: index === currentStep ? 'primary.500' : 'neutral.300',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                opacity: index === currentStep ? 1 : 0.5,
              }}
            />
          ))}
        </Box>
        
        {/* Compact navigation buttons */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {!isFirst && (
            <Button
              variant="plain"
              size="sm"
              onClick={handleBack}
              startDecorator={<ArrowBackIcon sx={{ fontSize: '1rem' }} />}
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 1.25,
                py: 0.5,
                minHeight: 28,
              }}
            >
              Back
            </Button>
          )}
          <Button
            variant="solid"
            size="sm"
            onClick={handleNext}
            endDecorator={!isLast && <ArrowForwardIcon sx={{ fontSize: '1rem' }} />}
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              px: 1.5,
              py: 0.5,
              minHeight: 28,
            }}
          >
            {isLast ? '✓ Done' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Card>
  );

  // Render as positioned tooltip or centered modal
  if (!targetRect || computedPlacement === 'center') {
    return (
      <Modal 
        open={run} 
        onClose={handleSkip}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ModalDialog
          sx={{
            maxWidth: 400,
            p: 0,
            overflow: 'hidden',
            borderRadius: '12px',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.25)',
          }}
        >
          {tooltipContent}
        </ModalDialog>
      </Modal>
    );
  }

  // Positioned tooltip with clear, non-blocking design
  return (
    <>
      {/* Minimal backdrop - very transparent, doesn't block clicks */}
      <Box
        ref={overlayRef}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'rgba(0, 0, 0, 0.15)', // Very light - barely visible
          zIndex: 10000,
          pointerEvents: 'none', // Completely non-blocking
          animation: 'backdropFadeIn 0.25s ease-out',
          '@keyframes backdropFadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 }
          },
        }}
      />

      {/* Subtle highlight ring - no overlay blocking, just a gentle indicator */}
      {targetRect && (
        <Box
          sx={{
            position: 'fixed',
            top: `${targetRect.top - 2}px`,
            left: `${targetRect.left - 2}px`,
            width: `${targetRect.width + 4}px`,
            height: `${targetRect.height + 4}px`,
            border: '2px solid',
            borderColor: 'primary.400',
            borderRadius: '6px',
            pointerEvents: 'none',
            zIndex: 9999, // Below tooltip but visible
            animation: 'gentlePulse 3s ease-in-out infinite',
            '@keyframes gentlePulse': {
              '0%, 100%': { 
                borderColor: 'var(--joy-palette-primary-400)',
                opacity: 0.6,
              },
              '50%': { 
                borderColor: 'var(--joy-palette-primary-500)',
                opacity: 0.9,
              }
            }
          }}
        />
      )}

      {/* Floating tooltip - positioned to side, never blocks target */}
      <Box 
        style={{
          ...getTooltipStyle(),
          pointerEvents: 'auto',
          zIndex: 10001,
        }}
      >
        {tooltipContent}
      </Box>
    </>
  );
}
